import { Component, input, signal } from '@angular/core';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-avatar',
  imports: [],
  template: `
    <div 
      [class]="avatarClasses"
      [attr.aria-label]="alt() || label()">
      @if (src() && !error()) {
        <img
          [src]="src()"
          [alt]="alt() || label()"
          (error)="onError()"
          class="w-full h-full object-cover rounded-full" />
      } @else {
        <div [class]="fallbackClasses">
          {{ getInitials() }}
        </div>
      }
      @if (badge()) {
        <span [class]="badgeClasses"></span>
      }
    </div>
  `,
  styles: []
})
export class AvatarComponent {
  src = input<string>('');
  label = input<string>('');
  alt = input<string>('');
  size = input<ComponentSize>('md');
  shape = input<'circle' | 'square'>('circle');
  badge = input<boolean>(false);
  badgeVariant = input<'online' | 'offline' | 'away'>('online');

  error = signal(false);

  onError(): void {
    this.error.set(true);
  }

  getInitials(): string {
    const label = this.label();
    if (!label) return '?';
    const parts = label.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return label.substring(0, 2).toUpperCase();
  }

  get avatarClasses(): string {
    const sizeMap = {
      xs: 'w-6 h-6',
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-16 h-16'
    };

    const classes = [
      'relative',
      'flex-shrink-0',
      'overflow-hidden',
      sizeMap[this.size()],
      this.shape() === 'circle' ? 'rounded-full' : 'rounded-lg'
    ];

    return classes.join(' ');
  }

  get fallbackClasses(): string {
    return 'w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-semibold';
  }

  get badgeClasses(): string {
    const sizeMap = {
      xs: 'w-1.5 h-1.5',
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
      xl: 'w-4 h-4'
    };

    const classes = [
      'absolute',
      'bottom-0',
      'right-0',
      'rounded-full',
      'border-2',
      'border-white',
      'dark:border-gray-800',
      sizeMap[this.size()]
    ];

    const variantMap = {
      online: 'bg-green-500',
      offline: 'bg-gray-400',
      away: 'bg-yellow-500'
    };
    classes.push(variantMap[this.badgeVariant()]);

    return classes.join(' ');
  }
}

